<template>
  <div v-if="visible" class="modal-backdrop" @click="handleBackdropClick">
    <div class="modal fade show d-block" tabindex="-1" role="dialog" style="z-index: 1050;">
      <div class="modal-dialog modal-xl" role="document">
        <div class="modal-content" @click.stop>
          <!-- Header del Modal -->
          <div class="modal-header bg-primary text-white">
            <div class="d-flex align-items-center w-100">
              <div class="flex-grow-1">
                <h4 class="modal-title mb-0 d-flex align-items-center">
                  <i class="fas fa-receipt" style="margin-right: 12px;"></i>
                  <span>Detalle Completo de Venta</span>
                </h4>
                <p class="mb-0 text-light small mt-1" v-if="sale">
                  <i class="fas fa-hashtag me-1"></i>
                  Transacción #{{ sale.idTransaccion }} - {{ formatDate(sale.fechaHora) }}
                </p>
              </div>
              <button 
                type="button" 
                class="btn btn-sm btn-light ms-2"
                @click="closeModal"
                title="Cerrar"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>

          <!-- Cuerpo del Modal -->
          <div class="modal-body p-0">
            <div v-if="sale" class="p-4">
              
              <!-- ============================================
                   SECCIÓN 1: INFORMACIÓN GENERAL DE LA VENTA
                   ============================================ -->
              <div class="card mb-4 border-0 shadow-sm">
                <div class="card-header section-header-modern bg-gradient-primary text-white">
                  <div class="d-flex align-items-center">
                    <div class="section-icon-wrapper">
                      <i class="fas fa-info-circle"></i>
                    </div>
                    <h6 class="mb-0">
                      INFORMACIÓN GENERAL DE LA VENTA
                    </h6>
                  </div>
                </div>
                <div class="card-body">
                  <div class="row">
                    <!-- Columna 1: Datos principales -->
                    <div class="col-lg-6">
                      <table class="table table-sm table-borderless">
                        <tbody>
                          <tr>
                            <td class="fw-bold text-muted" width="40%">
                              <i class="fas fa-hashtag me-1"></i> ID Transacción:
                            </td>
                            <td>{{ sale.idTransaccion }}</td>
                          </tr>
                          <tr>
                            <td class="fw-bold text-muted">
                              <i class="fas fa-calendar me-1"></i> Fecha y Hora:
                            </td>
                            <td>{{ formatDate(sale.fechaHora) }}</td>
                          </tr>
                          <tr>
                            <td class="fw-bold text-muted">
                              <i class="fas fa-building me-1"></i> Sucursal:
                            </td>
                            <td>{{ sale.nombreSucursal || 'No especificada' }}</td>
                          </tr>
                          <tr>
                            <td class="fw-bold text-muted">
                              <i class="fas fa-user-tie me-1"></i> Vendedor:
                            </td>
                            <td>{{ sale.nombreVendedor || 'No especificado' }}</td>
                          </tr>
                          <tr>
                            <td class="fw-bold text-muted">
                              <i class="fas fa-cash-register me-1"></i> Caja / POS:
                            </td>
                            <td>{{ sale.cajaPuntoDeVenta || 'No especificada' }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    
                    <!-- Columna 2: Datos del cliente y estados -->
                    <div class="col-lg-6">
                      <table class="table table-sm table-borderless">
                        <tbody>
                          <tr>
                            <td class="fw-bold text-muted" width="40%">
                              <i class="fas fa-user me-1"></i> Cliente:
                            </td>
                            <td>{{ sale.nombreCliente || 'No especificado' }}</td>
                          </tr>
                          <tr>
                            <td class="fw-bold text-muted">
                              <i class="fas fa-id-card me-1"></i> RUT Cliente:
                            </td>
                            <td>{{ sale.rutCliente || 'No especificado' }}</td>
                          </tr>
                          <tr>
                            <td class="fw-bold text-muted">
                              <i class="fas fa-envelope me-1"></i> Email:
                            </td>
                            <td>{{ sale.correoCliente || 'No especificado' }}</td>
                          </tr>
                          <tr>
                            <td class="fw-bold text-muted">
                              <i class="fas fa-credit-card me-1"></i> Método de Pago:
                            </td>
                            <td>{{ sale.metodoPago || 'No especificado' }}</td>
                          </tr>
                          <tr>
                            <td class="fw-bold text-muted">
                              <i class="fas fa-dollar-sign me-1"></i> Total Venta:
                            </td>
                            <td>
                              <span class="fw-bold text-success fs-5">
                                {{ formatCurrency(sale.ventaTotal) }}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  
                  <!-- Fila de estados -->
                  <div class="row mt-3">
                    <div class="col-12">
                      <div class="d-flex justify-content-around text-center">
                        <div class="badge-container">
                          <span class="fw-bold text-muted d-block mb-1">Estado de Venta:</span>
                          <span :class="getStatusBadgeClass(sale.estadoVenta)" class="fs-6">
                            {{ getStatusText(sale.estadoVenta) }}
                          </span>
                        </div>
                        <div class="badge-container">
                          <span class="fw-bold text-muted d-block mb-1">Estado de Cierre:</span>
                          <span :class="getCloseBadgeClass(sale.estadoCierre)" class="fs-6">
                            {{ sale.estadoCierre || '[ABIERTA]' }}
                          </span>
                        </div>
                        <div class="badge-container" v-if="sale.fechaCierre">
                          <span class="fw-bold text-muted d-block mb-1">Fecha de Cierre:</span>
                          <span class="text-info fs-6">{{ formatDate(sale.fechaCierre) }}</span>
                        </div>
                        <div class="badge-container" v-if="sale.responsableCierre">
                          <span class="fw-bold text-muted d-block mb-1">Responsable Cierre:</span>
                          <span class="text-secondary fs-6">{{ sale.responsableCierre }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ============================================
                   SECCIÓN 2: FORMAS DE PAGO DETALLADAS
                   ============================================ -->
              <div class="card mb-4 border-0 shadow-sm">
                <div class="card-header section-header-modern bg-gradient-success text-white">
                  <div class="d-flex align-items-center justify-content-between w-100">
                    <div class="d-flex align-items-center">
                      <div class="section-icon-wrapper">
                        <i class="fas fa-credit-card"></i>
                      </div>
                      <h6 class="mb-0">
                        FORMAS DE PAGO DETALLADAS
                      </h6>
                    </div>
                    <span class="badge bg-white text-success px-2 py-1">
                      <small>{{ getPaymentMethodsCount() }} método(s)</small>
                    </span>
                  </div>
                </div>
                <div class="card-body p-0">
                  <div class="table-responsive">
                    <table class="table table-striped table-hover mb-0">
                      <thead class="table-dark">
                        <tr>
                          <th class="text-center" width="15%">
                            <i class="fas fa-hashtag me-1"></i> N° Operación
                          </th>
                          <th width="25%">
                            <i class="fas fa-credit-card me-1"></i> Forma de Pago
                          </th>
                          <th width="35%">
                            <i class="fas fa-file-text me-1"></i> Detalles
                          </th>
                          <th class="text-end" width="25%">
                            <i class="fas fa-dollar-sign me-1"></i> Monto
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!hasPaymentMethods()" class="text-center text-muted">
                          <td colspan="4" class="py-4">
                            <i class="fas fa-info-circle fa-2x mb-2 d-block"></i>
                            <span>Esta venta no tiene información detallada de formas de pago</span>
                            <br>
                            <small class="text-muted">Método principal: {{ sale.metodoPago || 'No especificado' }}</small>
                          </td>
                        </tr>
                        <tr v-for="(payment, index) in getPaymentMethods()" :key="index" class="align-middle">
                          <td class="text-center">
                            <span class="badge bg-primary">{{ payment.numeroOperacion }}</span>
                          </td>
                          <td>
                            <span class="fw-bold">{{ payment.formaPago || 'No especificado' }}</span>
                          </td>
                          <td>
                            <small class="text-muted">
                              {{ payment.textoDetalleVenta || 'Sin detalles adicionales' }}
                            </small>
                          </td>
                          <td class="text-end">
                            <span class="fw-bold text-success">
                              {{ formatCurrency(payment.montoVenta) }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                      <tfoot v-if="hasPaymentMethods()" class="table-light">
                        <tr class="fw-bold">
                          <td colspan="3" class="text-end">Total de la Venta:</td>
                          <td class="text-end text-success fs-5">
                            {{ formatCurrency(sale.ventaTotal) }}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>

              <!-- ============================================
                   SECCIÓN 3: DETALLE DE PRODUCTOS / SERVICIOS
                   ============================================ -->
              <div class="card mb-4 border-0 shadow-sm">
                <div class="card-header section-header-modern bg-gradient-info">
                  <div class="d-flex align-items-center justify-content-between w-100">
                    <div class="d-flex align-items-center">
                      <div class="section-icon-wrapper">
                        <i class="fas fa-shopping-cart"></i>
                      </div>
                      <h6 class="mb-0">
                        DETALLE DE PRODUCTOS Y SERVICIOS
                      </h6>
                    </div>
                    <span class="badge bg-white text-dark px-2 py-1">
                      <small>{{ getSaleDetailsCount() }} ítem(s)</small>
                    </span>
                  </div>
                </div>
                <div class="card-body p-0">
                  <div class="table-responsive">
                    <table class="table table-striped table-hover mb-0">
                      <thead class="table-dark">
                        <tr>
                          <th width="12%">
                            <i class="fas fa-barcode me-1"></i> Código
                          </th>
                          <th width="25%">
                            <i class="fas fa-tag me-1"></i> Producto / Servicio
                          </th>
                          <th width="18%">
                            <i class="fas fa-folder me-1"></i> Categoría
                          </th>
                          <th class="text-center" width="8%">
                            <i class="fas fa-sort-numeric-up me-1"></i> Cant.
                          </th>
                          <th class="text-end" width="12%">
                            <i class="fas fa-dollar-sign me-1"></i> Precio Unit.
                          </th>
                          <th class="text-center" width="10%">
                            <i class="fas fa-percentage me-1"></i> Dcto.
                          </th>
                          <th class="text-end" width="15%">
                            <i class="fas fa-calculator me-1"></i> Total
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!hasSaleDetails()" class="text-center text-muted">
                          <td colspan="7" class="py-4">
                            <i class="fas fa-shopping-basket fa-2x mb-2 d-block"></i>
                            <span>Esta venta no tiene información detallada de productos o servicios</span>
                          </td>
                        </tr>
                        <tr v-for="(item, index) in getSaleDetails()" :key="index" class="align-middle">
                          <td>
                            <code class="text-primary">{{ item.codigo || 'SIN CÓDIGO' }}</code>
                          </td>
                          <td>
                            <div>
                              <span class="fw-bold">{{ item.detalle || 'Sin descripción' }}</span>
                              <br>
                              <small class="text-muted">{{ item.tipoItem || 'Tipo no especificado' }}</small>
                            </div>
                          </td>
                          <td>
                            <span class="badge badge-category bg-secondary text-white">
                              {{ item.categoriaProducto || 'Sin categoría' }}
                            </span>
                          </td>
                          <td class="text-center">
                            <span class="fw-bold">{{ item.cantidad || 0 }}</span>
                            <br>
                            <small class="text-muted">{{ item.cant || '' }}</small>
                          </td>
                          <td class="text-end">
                            {{ formatCurrency(item.precioUnitarioBruto || item.precio || 0) }}
                          </td>
                          <td class="text-center">
                            <span v-if="item.porcentajeDescuento > 0" class="text-danger">
                              {{ item.porcentajeDescuento }}%
                            </span>
                            <span v-else class="text-muted">—</span>
                          </td>
                          <td class="text-end">
                            <span class="fw-bold" :class="getItemTotalClass(item)">
                              {{ formatCurrency(calculateItemTotal(item)) }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                      <tfoot v-if="hasSaleDetails()" class="table-light">
                        <tr class="fw-bold">
                          <td colspan="6" class="text-end">Subtotal Items:</td>
                          <td class="text-end text-info fs-5">
                            {{ formatCurrency(calculateItemsSubtotal()) }}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>

              <!-- ============================================
                   SECCIÓN 4: INFORMACIÓN DE PLANES
                   ============================================ -->
              <div class="card mb-4 border-0 shadow-sm">
                <div class="card-header section-header-modern bg-gradient-warning">
                  <div class="d-flex align-items-center justify-content-between w-100">
                    <div class="d-flex align-items-center">
                      <div class="section-icon-wrapper">
                        <i class="fas fa-dumbbell"></i>
                      </div>
                      <h6 class="mb-0">
                        INFORMACIÓN DE MEMBRESÍA PAGADA
                      </h6>
                    </div>
                    <span class="badge bg-dark text-white px-2 py-1">
                      <small>{{ getPlansCount() }} plan(es)</small>
                    </span>
                  </div>
                </div>
                <div class="card-body p-0">
                  <div class="table-responsive">
                    <table class="table table-striped table-hover mb-0">
                      <thead class="table-dark">
                        <tr>
                          <th width="20%">
                            <i class="fas fa-tag me-1"></i> Plan
                          </th>
                          <th width="15%">
                            <i class="fas fa-laptop me-1"></i> Modalidad
                          </th>
                          <th width="15%">
                            <i class="fas fa-clock me-1"></i> Duración
                          </th>
                          <th class="text-end" width="12%">
                            <i class="fas fa-dollar-sign me-1"></i> Monto
                          </th>
                          <th width="12%">
                            <i class="fas fa-info-circle me-1"></i> Estado
                          </th>
                          <th width="13%">
                            <i class="fas fa-calendar me-1"></i> Fechas
                          </th>
                          <th width="13%">
                            <i class="fas fa-cog me-1"></i> Config
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!hasPlans()" class="text-center text-muted">
                          <td colspan="7" class="py-4">
                            <i class="fas fa-dumbbell fa-2x mb-2 d-block"></i>
                            <span>Esta venta no incluye membresías pagadas</span>
                            <br>
                            <small class="text-muted">Puede ser una venta sólo de productos o servicios únicos</small>
                          </td>
                        </tr>
                        <tr v-for="(plan, index) in getPlans()" :key="index" class="align-middle">
                          <td>
                            <div>
                              <span class="fw-bold">{{ plan.descripcionPlan || 'Sin descripción' }}</span>
                              <br>
                              <small class="text-muted">
                                {{ plan.familiaPlan || 'Sin familia' }} - {{ plan.subFamiliaPlan || 'Sin subfamilia' }}
                              </small>
                            </div>
                          </td>
                          <td>
                            <span class="badge badge-modality" :class="getModalityBadgeClass(plan.modalidad)">
                              {{ plan.modalidad || 'No especificada' }}
                            </span>
                            <br>
                            <small v-if="plan.multiSede" class="text-success">
                              <i class="fas fa-check-circle me-1"></i> MultiSede
                            </small>
                          </td>
                          <td>
                            <span class="fw-bold">{{ plan.duracion || 0 }} {{ plan.unidad || '' }}</span>
                            <br>
                            <small v-if="plan.sesiones" class="text-info">
                              {{ plan.nSesiones || 0 }} sesiones
                            </small>
                          </td>
                          <td class="text-end">
                            <span class="fw-bold text-success">{{ formatCurrency(plan.monto || 0) }}</span>
                            <br>
                            <small v-if="parsePlanDiscount(plan.dcto) > 0" class="text-danger">
                              Dcto: {{ parsePlanDiscount(plan.dcto).toFixed(0) }}%
                            </small>
                          </td>
                          <td>
                            <span class="badge-status" :class="getPlanStatusBadgeClass(plan.estadoProcesoPago)">
                              {{ plan.estadoProcesoPago || 'Sin estado' }}
                            </span>
                            <br>
                            <small v-if="plan.estadoDePlan === 'VIGENTE'" class="fw-bold text-warning">
                              Por Renovación
                            </small>
                          </td>
                          <td>
                            <small class="text-muted">
                              <div v-if="plan.fechaDeIngreso">
                                <strong>Ingreso:</strong><br>{{ formatShortDate(plan.fechaDeIngreso) }}
                              </div>
                              <div v-if="plan.fechaDeVencimiento">
                                <strong>Vence:</strong><br>{{ formatShortDate(plan.fechaDeVencimiento) }}
                              </div>
                            </small>
                          </td>
                          <td>
                            <small class="text-muted">
                              <div v-if="plan.turno">
                                <i class="fas fa-clock me-1"></i> Turno: {{ plan.turno }}
                              </div>
                              <div v-if="plan.congelar > 0">
                                <i class="fas fa-snowflake me-1 text-info"></i> {{ plan.congelar }} días congelable
                              </div>
                              <div v-if="plan.permiteRenovacion">
                                <i class="fas fa-sync me-1 text-success"></i> Renovable
                              </div>
                            </small>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <!-- ============================================
                   SECCIÓN 5: INFORMACIÓN ADICIONAL Y METADATOS
                   ============================================ -->
              <div class="card border-0 shadow-sm">
                <div class="card-header section-header-modern bg-gradient-secondary text-white">
                  <div class="d-flex align-items-center">
                    <div class="section-icon-wrapper">
                      <i class="fas fa-database"></i>
                    </div>
                    <h6 class="mb-0">
                      INFORMACIÓN ADICIONAL Y METADATOS
                    </h6>
                  </div>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-lg-6">
                      <h6 class="text-muted mb-3">
                        <i class="fas fa-file-code me-1"></i>Identificadores del Sistema
                      </h6>
                      <table class="table table-sm table-borderless">
                        <tbody>
                          <tr>
                            <td class="fw-bold text-muted" width="40%">ID Documento:</td>
                            <td><code>{{ sale._id || 'No disponible' }}</code></td>
                          </tr>
                          <tr v-if="sale.updatedAt">
                            <td class="fw-bold text-muted">Última Actualización:</td>
                            <td>{{ formatDate(sale.updatedAt) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div class="col-lg-6">
                      <h6 class="text-muted mb-3">
                        <i class="fas fa-chart-bar me-1"></i>Resumen de la Transacción
                      </h6>
                      <div class="d-flex justify-content-around text-center">
                        <div class="summary-item">
                          <div class="h4 text-primary mb-1">{{ getPaymentMethodsCount() }}</div>
                          <small class="text-muted">Formas de Pago</small>
                        </div>
                        <div class="summary-item">
                          <div class="h4 text-info mb-1">{{ getSaleDetailsCount() }}</div>
                          <small class="text-muted">Items Vendidos</small>
                        </div>
                        <div class="summary-item">
                          <div class="h4 text-warning mb-1">{{ getPlansCount() }}</div>
                          <small class="text-muted">Planes Incluidos</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <!-- Footer del Modal -->
          <div class="modal-footer bg-light">
            <div class="d-flex justify-content-between w-100 align-items-center">
              <div>
                <small class="text-muted">
                  <i class="fas fa-info-circle me-1"></i>
                  Información obtenida de la ventas hecha en el gimnasio
                </small>
              </div>
              <div>
                <button 
                  type="button" 
                  class="btn btn-primary me-2"
                  @click="printDetails"
                  title="Abrir documento con opciones de impresión y PDF"
                >
                  <i class="fas fa-external-link-alt me-1"></i>
                  Ver Documento
                </button>
                <button 
                  type="button" 
                  class="btn btn-secondary"
                  @click="closeModal"
                >
                  <i class="fas fa-times me-1"></i>
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de impresión profesional -->
    <VoucherPrintModal
      :visible="showPrintModal"
      :sale-data="sale"
      @close="handlePrintModalClose"
    />
  </div>
</template>

<script>
import VoucherPrintModal from './VoucherPrintModal.vue';

export default {
  name: 'SaleDetailModal',
  components: {
    VoucherPrintModal
  },
  props: {
    sale: {
      type: Object,
      default: null
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showPrintModal: false
    };
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal) {
          // Bloquear scroll del body cuando el modal se abre
          document.body.style.overflow = 'hidden';
          document.body.style.paddingRight = '0px'; // Evitar salto por scrollbar
          // Agregar listener para la tecla Escape
          document.addEventListener('keydown', this.handleEscapeKey);
          // Resetear el estado del modal de impresión cuando se abre el modal principal
          this.showPrintModal = false;
        } else {
          // Restaurar scroll del body cuando el modal se cierra
          document.body.style.overflow = '';
          document.body.style.paddingRight = '';
          // Remover listener para la tecla Escape
          document.removeEventListener('keydown', this.handleEscapeKey);
          // Asegurar que el modal de impresión se cierre cuando se cierra el modal principal
          this.showPrintModal = false;
        }
      },
      immediate: true
    }
  },
  beforeDestroy() {
    // Asegurar que se restaure el scroll del body si el componente se destruye
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    // Remover listener para la tecla Escape si existe
    document.removeEventListener('keydown', this.handleEscapeKey);
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    
    handleBackdropClick() {
      // Cerrar modal al hacer clic en el backdrop, solo si no hay modal de impresión abierto
      if (this.showPrintModal) {
        console.log('🖱️ SaleDetailModal: Click en backdrop ignorado - modal de impresión abierto');
        return;
      }
      console.log('🖱️ SaleDetailModal: Click en backdrop - cerrando modal de detalle');
      this.closeModal();
    },
    
    handleEscapeKey(event) {
      // Cerrar modal al presionar Escape, solo si no hay modal de impresión abierto
      if (event.key === 'Escape' || event.keyCode === 27) {
        if (this.showPrintModal) {
          // Si el modal de impresión está abierto, no hacer nada
          // El VoucherPrintModal manejará el Escape
          console.log('🔑 SaleDetailModal: Escape ignorado - modal de impresión abierto');
          return;
        }
        console.log('🔑 SaleDetailModal: Escape presionado - cerrando modal de detalle');
        this.closeModal();
      }
    },
    
    // Métodos de formato
    // CORREGIDO v2.5.1: Función para convertir fechas chilenas DD-MM-YYYY a formato JavaScript
    parseChileanDate(dateString) {
      if (!dateString || dateString.trim() === '') {
        return null;
      }
      
      try {
        // Formato esperado: "31-05-2024 15:41:36" o "31-05-2024"
        const dateTimeParts = dateString.trim().split(' ');
        const datePart = dateTimeParts[0]; // "31-05-2024"
        const timePart = dateTimeParts[1] || '00:00:00'; // "15:41:36" o "00:00:00"
        
        // Separar fecha DD-MM-YYYY
        const dateComponents = datePart.split('-');
        if (dateComponents.length !== 3) {
          console.warn('⚠️ Formato de fecha inválido:', dateString);
          return null;
        }
        
        const day = parseInt(dateComponents[0]);
        const month = parseInt(dateComponents[1]) - 1; // JavaScript usa meses 0-11
        const year = parseInt(dateComponents[2]);
        
        // Separar tiempo HH:mm:ss
        const timeComponents = timePart.split(':');
        const hours = parseInt(timeComponents[0]) || 0;
        const minutes = parseInt(timeComponents[1]) || 0;
        const seconds = parseInt(timeComponents[2]) || 0;
        
        // Crear objeto Date válido
        const jsDate = new Date(year, month, day, hours, minutes, seconds);
        
        // Validar que la fecha sea válida
        if (isNaN(jsDate.getTime())) {
          console.warn('⚠️ Fecha resultante inválida:', dateString, '→', jsDate);
          return null;
        }
        
        return jsDate;
      } catch (error) {
        console.error('❌ Error al parsear fecha chilena:', dateString, error);
        return null;
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return 'No especificada';
      
      const date = this.parseChileanDate(dateString);
      if (!date) {
        console.warn('⚠️ No se pudo formatear la fecha:', dateString);
        return 'Fecha inválida';
      }
      
      return date.toLocaleString('es-CL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    formatShortDate(dateString) {
      if (!dateString) return 'No especificada';
      
      const date = this.parseChileanDate(dateString);
      if (!date) {
        console.warn('⚠️ No se pudo formatear la fecha corta:', dateString);
        return 'Fecha inválida';
      }
      
      return date.toLocaleDateString('es-CL');
    },
    
    // NUEVO v2.5.2: Método para parsear correctamente el descuento de planes
    parsePlanDiscount(dcto) {
      if (!dcto || dcto === '0' || dcto === 0) {
        return 0;
      }
      
      try {
        let discountValue = dcto;
        
        // Si es string, manejar tanto formato europeo (coma) como americano (punto)
        if (typeof dcto === 'string') {
          // Reemplazar coma por punto para parseFloat
          discountValue = dcto.replace(',', '.');
        }
        
        // Convertir a número
        const parsed = parseFloat(discountValue);
        
        // Validar que sea un número válido y esté en rango esperado (0 a 1)
        if (isNaN(parsed) || parsed < 0 || parsed > 1) {
          console.warn('⚠️ Valor de descuento fuera de rango:', dcto, '→', parsed);
          return 0;
        }
        
        // Convertir a porcentaje (multiplicar por 100)
        const percentage = parsed * 100;
        
        console.log('💰 Descuento parseado:', { original: dcto, parsed, percentage: `${percentage.toFixed(0)}%` });
        
        return percentage;
      } catch (error) {
        console.error('❌ Error parseando descuento del plan:', dcto, error);
        return 0;
      }
    },
    
    formatCurrency(value) {
      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP'
      }).format(value || 0);
    },
    
    // Métodos para obtener datos de las secciones
    getPaymentMethods() {
      return this.sale?.infoVenta?.venta || [];
    },
    
    hasPaymentMethods() {
      return this.getPaymentMethods().length > 0;
    },
    
    getPaymentMethodsCount() {
      return this.getPaymentMethods().length;
    },
    
    getSaleDetails() {
      return this.sale?.infoVenta?.detalleVenta || [];
    },
    
    hasSaleDetails() {
      return this.getSaleDetails().length > 0;
    },
    
    getSaleDetailsCount() {
      return this.getSaleDetails().length;
    },
    
    getPlans() {
      return this.sale?.infoVenta?.plan || [];
    },
    
    hasPlans() {
      return this.getPlans().length > 0;
    },
    
    getPlansCount() {
      return this.getPlans().length;
    },
    
    // Métodos para estilos de badges
    getStatusBadgeClass(status) {
      return {
        'badge': true,
        'bg-success-pastel': status === '' || status === null || status === undefined,
        'bg-danger-pastel': status === '[ANULADA]'
      };
    },
    
    getStatusText(status) {
      if (status === '' || status === null || status === undefined) {
        return '[VIGENTE]';
      }
      return status || 'Sin estado';
    },
    
    getCloseBadgeClass(status) {
      return {
        'badge': true,
        'bg-success': status === '[CERRADA]',
        'bg-warning text-dark': status !== '[CERRADA]'
      };
    },
    
    getModalityBadgeClass(modality) {
      const baseClasses = 'text-white';
      switch (modality) {
        case 'ON-LINE':
          return baseClasses + ' bg-primary';
        case 'PRESENCIAL':
          return baseClasses + ' bg-success';
        default:
          return baseClasses + ' bg-secondary';
      }
    },
    
    getPlanStatusBadgeClass(status) {
      return {
        'text-white': status !== '[RENOVACION]' && status !== '[PROCESANDO]',
        'text-dark': status === '[RENOVACION]' || status === '[PROCESANDO]',
        'bg-success': status === '[PAGO]',
        'bg-info': status === '[PENDIENTE]',
        'bg-warning': status === '[RENOVACION]' || status === '[PROCESANDO]',
        'bg-danger': status === '[RECHAZADO]' || status === '[ERROR]',
        'bg-secondary': !status
      };
    },
    
    getItemTotalClass(item) {
      const total = this.calculateItemTotal(item);
      return {
        'text-success': total > 0,
        'text-danger': total < 0,
        'text-muted': total === 0
      };
    },
    
    // Métodos de cálculo
    calculateItemTotal(item) {
      if (!item) return 0;
      return item.precio || item.totalNeto || (item.cantidad * (item.precioUnitarioBruto || 0)) || 0;
    },
    
    calculateItemsSubtotal() {
      return this.getSaleDetails().reduce((sum, item) => {
        return sum + this.calculateItemTotal(item);
      }, 0);
    },
    
    // Método simplificado para abrir el comprobante
    printDetails() {
      // Pequeño delay para mejor transición visual
      this.$nextTick(() => {
        this.showPrintModal = true;
      });
    },
    
    // Método para manejar el cierre del modal de impresión
    handlePrintModalClose() {
      console.log('🖨️ Cerrando modal de impresión');
      this.showPrintModal = false;
      // No cerrar el modal principal, solo el de impresión
      // El modal de detalle de venta permanece abierto
    },
    
    // Método para mostrar mensajes de éxito
    showSuccessMessage(title, text) {
      if (this.$swal) {
        this.$swal.fire({
          icon: 'success',
          title: title,
          text: text,
          confirmButtonColor: '#6bd098',
          timer: 4000,
          timerProgressBar: true,
          toast: true,
          position: 'top-end',
          showConfirmButton: false
        });
      }
    },
    
    // Método para mostrar mensajes de error
    showErrorMessage(title, text) {
      if (this.$swal) {
        this.$swal.fire({
          icon: 'error',
          title: title,
          text: text,
          confirmButtonColor: '#6bd098'
        });
      } else {
        alert(`Error - ${title}: ${text}`);
      }
    },
    
    // Cargar plantilla HTML desde archivo externo
    async loadPrintTemplate() {
      try {
        console.log('📄 Cargando plantilla HTML desde public/templates/');
        
        // Usar ruta absoluta desde la carpeta public
        const templatePath = '/templates/ThermalPrintTemplate.html';
        
        // Cargar el contenido del archivo
        const response = await fetch(templatePath);
        
        if (!response.ok) {
          throw new Error(`Error al cargar plantilla: ${response.status} ${response.statusText}`);
        }
        
        const templateHTML = await response.text();
        
        if (!templateHTML || templateHTML.trim() === '') {
          throw new Error('La plantilla HTML está vacía');
        }
        
        console.log('✅ Plantilla HTML cargada exitosamente');
        return templateHTML;
        
      } catch (error) {
        console.error('❌ Error cargando plantilla HTML:', error);
        console.log('🔄 Usando plantilla de respaldo...');
        
        // Fallback: usar plantilla básica si no se puede cargar el archivo
        return this.getFallbackTemplate();
      }
    },
    
    // Plantilla de respaldo en caso de error
    getFallbackTemplate() {
      return `<!DOCTYPE html>
<html lang="es">
<head>
    <meta charset="UTF-8">
    <title>Comprobante de Venta - CentralGym</title>
    <style>
        body { font-family: 'Courier New', monospace; font-size: 12px; margin: 10px; }
        .header { text-align: center; border-bottom: 1px solid #000; padding-bottom: 10px; }
        .info { margin: 10px 0; }
        .total { font-weight: bold; border-top: 1px solid #000; padding-top: 5px; }
    </style>
</head>
<body>
    <div class="header">
        <h3>COMPROBANTE DE VENTA</h3>
        <p>CentralGym Spa</p>
        <p>N° @[NUMERO_DOCUMENTO]@</p>
    </div>
    <div class="info">
        <p><strong>Cliente:</strong> @[NOMBRE_CLIENTE]@</p>
        <p><strong>Vendedor:</strong> @[NOMBRE_VENDEDOR]@</p>
        <p><strong>Fecha:</strong> @[FECHA_DOCUMENTO]@ @[HORA_DOCUMENTO]@</p>
        <p><strong>Método de Pago:</strong> @[METODO_PAGO]@</p>
    </div>
    <div class="total">
        <p><strong>TOTAL: @[TOTAL_VENTA]@</strong></p>
        <p>Estado: @[ESTADO_VENTA]@</p>
    </div>
</body>
</html>`;
    },
    

    
    // Preparar datos para la impresión
    preparePrintData() {
      if (!this.sale) {
        console.error('❌ No hay datos de venta para preparar');
        return {};
      }
      
      console.log('📊 Preparando datos de venta:', this.sale);
      
      const now = new Date();
      // CORREGIDO v2.5.1: Usar parseChileanDate para fechas del API
      const saleDate = this.sale.fechaHora ? this.parseChileanDate(this.sale.fechaHora) : now;
      
      // Si no se pudo parsear la fecha de la venta, usar fecha actual
      const validSaleDate = saleDate || now;
      
      // Obtener ID de transacción (puede estar en diferentes formatos)
      let transactionId = '000000';
      if (this.sale.idTransaccion) {
        if (typeof this.sale.idTransaccion === 'object' && this.sale.idTransaccion.$numberLong) {
          transactionId = this.sale.idTransaccion.$numberLong;
        } else {
          transactionId = this.sale.idTransaccion.toString();
        }
      }
      
      // CORREGIDO: Calcular totales con fórmulas correctas
      const total = parseFloat(this.sale.ventaTotal) || 0;
      
      // Valor Neto = Total Venta / 1.19
      const neto = Math.round(total / 1.19);
      
      // IVA = Valor Neto * 19%
      const iva = Math.round(neto * 0.19);
      
      console.log('💰 Cálculos financieros corregidos:', { 
        total, 
        neto, 
        iva,
        verificacion: neto + iva,
        formula: 'Neto = Total/1.19, IVA = Neto*0.19'
      });
      
      const printData = {
        // Información de fecha y documento (CORREGIDO: usar validSaleDate parseado)
        FECHA_DOCUMENTO: validSaleDate.toLocaleDateString('es-CL'),
        HORA_DOCUMENTO: validSaleDate.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' }),
        NUMERO_DOCUMENTO: transactionId,
        
        // Información de la empresa (datos ficticios basados en la imagen)
        RUT_EMPRESA: '77.935.821-6',
        NOMBRE_EMPRESA: 'CentralGym Spa',
        GIRO_EMPRESA: 'Gestión Administrativa y Control de Acceso para Gimnasios',
        DIRECCION_EMPRESA: 'Av. Club Hípico #4476, Piso 7, Torre 6, Pedro Aguirre Cerda.',
        COMUNA_EMPRESA: 'Santiago, RM',
        TELEFONO_EMPRESA: '(+56) 9 6438 2609',
        HORARIO_EMPRESA: 'Lunes a Viernes: 09:00 a 19:00, Sábado: CERRADO, Domingo: CERRADO',
        ESTADO_EMPRESA: 'ABIERTO',
        SITIO_WEB: 'www.2tec.cl',
        
        // Información de la transacción
        ID_TRANSACCION: transactionId,
        NOMBRE_CLIENTE: this.sale.nombreCliente || 'CentralGym Demo',
        RUT_CLIENTE: this.sale.rutCliente || '', // RUT del cliente (vacío si no existe)
        NOMBRE_VENDEDOR: this.sale.nombreVendedor || 'Soporte: CentralGym App',
        METODO_PAGO: this.sale.metodoPago || '[TARJETA DE CRÉDITO]',
        
        // Totales calculados
        VALOR_NETO: this.formatCurrencyForPrint(neto),
        IVA_VENTA: this.formatCurrencyForPrint(iva),
        TOTAL_VENTA: this.formatCurrencyForPrint(total),
        MEDIO_RECIBIDO: this.formatCurrencyForPrint(total),
        VUELTO: '$0',
        
        // Estado de la venta
        ESTADO_VENTA: this.getStatusText(this.sale.estadoVenta),
        CLASE_ESTADO: this.getStatusText(this.sale.estadoVenta) === '[VIGENTE]' ? 'status-vigente' : 'status-anulada',
        
        // Items de venta (se procesarán por separado)
        ITEMS_VENTA: this.generateItemsHTML()
      };
      
      console.log('✅ Datos preparados para impresión:', printData);
      console.log('📋 RUT del cliente:', printData.RUT_CLIENTE || '(no proporcionado)');
      return printData;
    },
    
    // Generar HTML para los items de venta (SOLO del detalle de productos/servicios)
    generateItemsHTML() {
      let itemsHTML = '';
      let itemCount = 0;
      let totalItemsSum = 0; // Para validar que coincida con el total de la venta
      
      console.log('🛒 Generando HTML para items de venta del comprobante...');
      console.log('ℹ️ NOTA: Solo se incluyen items del "Detalle de Productos y Servicios"');
      console.log('ℹ️ Los planes de membresía ya están incluidos en el detalle, no se duplican');
      
      // SOLO procesar productos/servicios del detalleVenta (NO duplicar planes)
      const saleDetails = this.getSaleDetails();
      console.log('📦 Items del detalle de productos/servicios encontrados:', saleDetails.length);
      
      if (saleDetails.length > 0) {
        saleDetails.forEach((item, index) => {
          // Obtener precio unitario correcto
          let precioUnitario = 0;
          if (item.precioUnitarioBruto) {
            precioUnitario = parseFloat(item.precioUnitarioBruto);
          } else if (item.precio) {
            precioUnitario = parseFloat(item.precio);
          } else if (item.precioUnitario) {
            precioUnitario = parseFloat(item.precioUnitario);
          }
          
          // Obtener cantidad
          const cantidad = parseInt(item.cantidad || item.cant || 1);
          
          // Calcular total del item (precio unitario × cantidad)
          let totalItem = precioUnitario * cantidad;
          
          // Si existe totalNeto o total calculado, usarlo como referencia
          if (item.totalNeto) {
            totalItem = parseFloat(item.totalNeto);
          } else if (item.total) {
            totalItem = parseFloat(item.total);
          }
          
          totalItemsSum += totalItem;
          
          console.log(`📦 Item ${index + 1}:`, { 
            codigo: item.codigo, 
            detalle: item.detalle, 
            categoria: item.categoriaProducto,
            precioUnitario, 
            cantidad, 
            totalItem,
            tipo: 'producto/servicio'
          });
          
          itemsHTML += `
            <div class="item">
              <div class="item-header">
                <span>${item.codigo || `0100${index + 2}`}</span>
                <span>${this.formatCurrencyForPrint(precioUnitario)}</span>
              </div>
              <div class="item-details">${item.detalle || item.descripcion || 'PRODUCTO/SERVICIO'}</div>
              <div class="item-price">
                <span>Cant: ${cantidad}</span>
                <span>Total: ${this.formatCurrencyForPrint(totalItem)}</span>
              </div>
            </div>
          `;
          itemCount++;
        });
        
        console.log('✅ Items del detalle de productos/servicios procesados correctamente');
        
      } else {
        // Si no hay items específicos en detalleVenta, generar item genérico
        const totalVenta = parseFloat(this.sale.ventaTotal) || 500;
        console.log('📦 Sin items en detalleVenta, generando item genérico con total de venta:', totalVenta);
        
        itemsHTML = `
          <div class="item">
            <div class="item-header">
              <span>01002</span>
              <span>${this.formatCurrencyForPrint(totalVenta)}</span>
            </div>
            <div class="item-details">VENTA DE GIMNASIO</div>
            <div class="item-price">
              <span>Cant: 1</span>
              <span>Total: ${this.formatCurrencyForPrint(totalVenta)}</span>
            </div>
          </div>
        `;
        itemCount = 1;
        totalItemsSum = totalVenta;
      }
      
      // Log informativo sobre planes (solo para información, NO se procesan en el comprobante)
      const plans = this.getPlans();
      if (plans.length > 0) {
        console.log(`ℹ️ Se encontraron ${plans.length} plan(es) de membresía en "Información de Membresía Pagada"`);
        console.log('ℹ️ Estos planes NO se incluyen en el comprobante porque ya están en el detalle de productos/servicios');
        plans.forEach((plan, index) => {
          console.log(`🏋️ Plan ${index + 1} (SOLO INFO):`, { 
            descripcion: plan.descripcionPlan, 
            monto: plan.monto,
            nota: 'No incluido en comprobante - solo informativo'
          });
        });
      }
      
      // Validación: verificar que la suma de items coincida aproximadamente con el total de la venta
      const totalVenta = parseFloat(this.sale.ventaTotal) || 0;
      const diferencia = Math.abs(totalItemsSum - totalVenta);
      
      if (diferencia > 1 && totalVenta > 0) { // Permitir diferencia de $1 por redondeos
        console.warn('⚠️ Discrepancia en totales:', {
          totalVenta,
          totalItemsSum,
          diferencia,
          mensaje: 'La suma de items del detalle no coincide con el total de la venta'
        });
      }
      
      console.log(`✅ HTML de comprobante generado:`, {
        itemsProcesados: itemCount,
        totalItemsSum,
        totalVenta,
        coincidencia: diferencia <= 1 ? '✅' : '⚠️',
        fuente: 'Solo detalle de productos/servicios (sin duplicar planes)'
      });
      
      return itemsHTML;
    },
    
    // Reemplazar parámetros en la plantilla
    replaceTemplateParameters(template, data) {
      let processedTemplate = template;
      let replacementCount = 0;
      
      console.log('🔄 Iniciando reemplazo de parámetros...');
      console.log('📋 Parámetros a reemplazar:', Object.keys(data));
      
      // Reemplazar todos los parámetros
      Object.keys(data).forEach(key => {
        const parameter = `@[${key}]@`;
        const value = String(data[key] || ''); // Convertir a string
        
        // Contar cuántas veces aparece el parámetro
        const occurrences = (processedTemplate.match(new RegExp(parameter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g')) || []).length;
        
        if (occurrences > 0) {
          console.log(`✅ Reemplazando ${parameter}: "${value}" (${occurrences} ocurrencias)`);
          processedTemplate = processedTemplate.replace(new RegExp(parameter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
          replacementCount += occurrences;
        } else {
          console.log(`⚠️ Parámetro ${parameter} no encontrado en plantilla`);
        }
      });
      
      // Verificar si quedaron parámetros sin reemplazar
      const remainingParameters = processedTemplate.match(/@\[[^\]]+\]@/g);
      if (remainingParameters) {
        console.warn('⚠️ Parámetros sin reemplazar:', remainingParameters);
      }
      
      console.log(`✅ Reemplazo completado: ${replacementCount} parámetros reemplazados`);
      
      return processedTemplate;
    },
    
    // Formatear moneda para impresión
    formatCurrencyForPrint(value) {
      const number = parseFloat(value) || 0;
      
      // Formatear con pesos chilenos
      const formatted = new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(number);
      
      // Logs para debugging (solo para valores significativos)
      if (number > 0) {
        console.log(`💰 Formateando moneda: ${value} → ${formatted}`);
      }
      
      return formatted;
    }
  }
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 9998;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
  padding: 1rem;
  animation: fadeIn 0.3s ease-out;
}

/* Ajustar z-index cuando el modal de impresión está abierto */
.modal-backdrop:has(+ .print-modal-active) {
  z-index: 9995;
}

.modal.fade.show.d-block {
  position: static;
  display: block !important;
  z-index: 9999;
  width: 100%;
  height: auto;
  max-height: none;
  overflow: visible;
}

.modal-dialog {
  position: relative;
  width: 100%;
  max-width: 95vw;
  margin: 0 auto;
  display: flex;
  align-items: center;
  min-height: calc(100vh - 2rem);
  z-index: 10000;
}

.modal-content {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 95vh;
  border-radius: 0.75rem;
  box-shadow: 0 2rem 4rem rgba(0, 0, 0, 0.3);
  background-color: #fff;
  border: none;
  z-index: 10001;
  animation: slideInUp 0.3s ease-out;
}

.modal-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
  border-radius: 0.75rem 0.75rem 0 0;
  padding: 1rem 1.25rem !important;
}

.modal-header .modal-title {
  font-size: 1.125rem !important;
  font-weight: 500 !important;
}

.modal-header .modal-title i {
  font-size: 1rem !important;
}

.modal-body {
  flex: 1 1 auto;
  overflow-y: auto;
  max-height: calc(95vh - 140px); /* Ajustado para header y footer */
  padding: 0;
}

.modal-footer {
  flex-shrink: 0;
  border-radius: 0 0 0.75rem 0.75rem;
}

.badge-container {
  min-width: 120px;
}

.summary-item {
  min-width: 80px;
}

/* Animaciones */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(50px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Estilos modernos para títulos de secciones */
.section-header-modern {
  padding: 0.625rem 1rem !important;
  min-height: 2.75rem;
  border-radius: 0.5rem 0.5rem 0 0 !important;
  border: none !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
}

.section-header-modern h6 {
  font-size: 0.875rem !important;
  letter-spacing: 0.025em;
  line-height: 1;
  margin: 0 !important;
  font-weight: 600 !important;
}

.section-icon-wrapper {
  width: 26px;
  height: 26px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 0.625rem !important;
}

.section-icon-wrapper i {
  font-size: 0.875rem !important;
  line-height: 1;
}

/* Colores sólidos para mejor legibilidad */
.bg-gradient-primary {
  background: #0d6efd !important;
}

.bg-gradient-success {
  background: #198754 !important;
}

.bg-gradient-info {
  background: #17a2b8 !important;
}

.bg-gradient-warning {
  background: #ffc107 !important;
}

.bg-gradient-warning h6,
.bg-gradient-warning .section-icon-wrapper i {
  color: #212529 !important; /* Texto oscuro para mejor contraste en amarillo */
}

.bg-gradient-secondary {
  background: #6c757d !important;
}

/* Estilos para badges personalizados */
.bg-success-pastel {
  background-color: #d4edda !important;
  color: #155724 !important;
  border: 1px solid #c3e6cb !important;
  font-weight: 600 !important;
  padding: 0.375rem 0.75rem !important;
  border-radius: 0.375rem !important;
}

.bg-danger-pastel {
  background-color: #f8d7da !important;
  color: #721c24 !important;
  border: 1px solid #f5c6cb !important;
  font-weight: 600 !important;
  padding: 0.375rem 0.75rem !important;
  border-radius: 0.375rem !important;
}

/* Estilos específicos para badges con texto completo */
.badge-category {
  display: inline-block !important;
  padding: 0.25rem 0.75rem !important;
  font-size: 0.75rem !important;
  font-weight: 500 !important;
  white-space: nowrap !important;
  min-width: fit-content !important;
  line-height: 1.2 !important;
}

.badge-modality {
  display: inline-block !important;
  padding: 0.25rem 0.75rem !important;
  font-size: 0.75rem !important;
  font-weight: 500 !important;
  white-space: nowrap !important;
  min-width: fit-content !important;
  line-height: 1.2 !important;
}

.badge-status {
  display: inline-block !important;
  padding: 0.25rem 0.75rem !important;
  font-size: 0.75rem !important;
  font-weight: 500 !important;
  white-space: nowrap !important;
  min-width: fit-content !important;
  line-height: 1.2 !important;
}

/* Estilos para cards modernizadas */
.card {
  border-radius: 0.5rem !important;
  overflow: hidden;
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

/* Estilos para tamaños de fuente en tablas */
.table {
  font-size: 0.875rem !important;
}

.table thead th {
  font-size: 0.8125rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.025em;
}

.table tbody td {
  font-size: 0.8125rem !important;
}

.table tbody td code {
  font-size: 0.75rem !important;
}

.table tbody td small {
  font-size: 0.75rem !important;
}

.table tbody td .fw-bold {
  font-size: 0.8125rem !important;
}

.table tfoot td {
  font-size: 0.875rem !important;
}

/* Mejora de contraste para badges en tablas */
.table td .badge {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Animación suave para badges */
.badge {
  transition: all 0.2s ease;
  font-size: 0.75rem !important;
  padding: 0.25rem 0.5rem !important;
}

.badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
}

/* Scroll personalizado para el modal-body */
.modal-body::-webkit-scrollbar {
  width: 8px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Estilos responsive */
@media (max-width: 768px) {
  .modal-backdrop {
    padding: 0.5rem;
  }
  
  .modal-dialog {
    max-width: 98vw;
    min-height: calc(100vh - 1rem);
  }
  
  .modal-content {
    max-height: 98vh;
    border-radius: 0.5rem;
  }
  
  .modal-body {
    max-height: calc(98vh - 120px);
    padding: 0;
  }
  
  .table-responsive {
    font-size: 0.875rem;
  }
  
  .badge-container {
    min-width: 100px;
    margin-bottom: 0.5rem;
  }
  
  .modal-header h4 {
    font-size: 1.1rem;
  }
  
  .modal-header p {
    font-size: 0.9rem;
  }
  
  /* Headers modernos en móvil */
  .section-header-modern {
    padding: 0.5rem 0.75rem !important;
    min-height: 2.5rem;
  }
  
  .section-header-modern h6 {
    font-size: 0.8rem !important;
  }
  
  .section-icon-wrapper {
    width: 22px;
    height: 22px;
    margin-right: 0.5rem !important;
  }
  
  .section-icon-wrapper i {
    font-size: 0.75rem !important;
  }
  
  /* Badges en móvil */
  .badge-category,
  .badge-modality,
  .badge-status {
    font-size: 0.7rem !important;
    padding: 0.2rem 0.5rem !important;
  }
}

@media (max-width: 576px) {
  .modal-backdrop {
    padding: 0.25rem;
  }
  
  .modal-dialog {
    max-width: 99vw;
    min-height: calc(100vh - 0.5rem);
  }
  
  .modal-content {
    max-height: 99vh;
    border-radius: 0.25rem;
  }
  
  .modal-body {
    max-height: calc(99vh - 100px);
  }
  
  .badge-container {
    min-width: 80px;
  }
}

/* Mejoras para pantallas grandes */
@media (min-width: 1200px) {
  .modal-dialog {
    max-width: 85vw;
  }
}

@media (min-width: 1400px) {
  .modal-dialog {
    max-width: 80vw;
  }
}

/* Estilos para impresión */
@media print {
  .modal-backdrop,
  .modal-header,
  .modal-footer {
    display: none !important;
  }
  
  .modal-dialog {
    max-width: 100% !important;
    margin: 0 !important;
  }
  
  .modal-content {
    box-shadow: none !important;
    border: none !important;
  }
  
  .card {
    page-break-inside: avoid;
  }
}
</style> 